import type { ProviderFileLimit } from '@/api';
import type { SignatureUploadEffectiveFile } from '@/types/contractManagement/signatureFlow';

export type RevisionWithOptionalPreview = {
  file_name: string;
  size_b?: number | null;
  preview_file_size_b?: number | null;
  preview_status?: string | null;
};

export function getFileExtension(fileName: string): string | null {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return null;
  }

  return fileName.slice(lastDotIndex + 1).toLowerCase();
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function hasPreviewFileSize(
  revision?: { preview_file_size_b?: number | null } | null
): boolean {
  return revision?.preview_file_size_b != null;
}

export function buildEffectiveFileFromRevision(
  revision: RevisionWithOptionalPreview
): SignatureUploadEffectiveFile {
  const hasPreviewFile = hasPreviewFileSize(revision);

  return {
    preview_status: revision.preview_status ?? null,
    has_preview_file: hasPreviewFile,
    size_b: revision.preview_file_size_b ?? revision.size_b ?? null,
    file_extension: hasPreviewFile ? 'pdf' : getFileExtension(revision.file_name),
  };
}

export function getFileLimitViolationReason(
  input: SignatureUploadEffectiveFile,
  limits?: ProviderFileLimit | null
): string | null {
  if (!limits) return null;

  const allowedExtensions = limits.allowed_file_extensions
    .map(extension => extension.trim().replace(/^\./, '').toLowerCase())
    .filter(extension => extension.length > 0);

  if (
    allowedExtensions.length > 0 &&
    (!input.file_extension || !allowedExtensions.includes(input.file_extension.toLowerCase()))
  ) {
    return 'File does not have an appropriate file type.';
  }

  const maxSizeBytes = limits.max_size_b > 0 ? limits.max_size_b : null;
  if (maxSizeBytes !== null && typeof input.size_b === 'number' && input.size_b > maxSizeBytes) {
    return `File exceeds maximum allowed size of ${formatFileSize(maxSizeBytes)}.`;
  }

  return null;
}
